// src/modules/materials/hooks/useMaterialSelector.js
// Malzeme/Hizmet seçici veri yönetimi hook'u

import { useState, useEffect, useMemo } from 'react';
import useCachedCollection from '../../../hooks/useCachedCollection';

export const useMaterialSelector = (isOpen) => {
    const [currentTab, setCurrentTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Stok ve Hizmet verileri: paylaşımlı oturum cache'i. Bu seçici servis ve satış
    // formlarından sık sık açılıyordu ve her açılışta iki koleksiyonu (stocks +
    // services) baştan okuyup kalıcı listener bırakıyordu.
    const { data: stocksRaw } = useCachedCollection('stocks');
    const { data: servicesRaw } = useCachedCollection('services');

    const stocks = useMemo(
        () => [...stocksRaw].sort((a, b) => (a.urunAdi || '').localeCompare(b.urunAdi || '', 'tr')),
        [stocksRaw]
    );
    const services = useMemo(
        () => [...servicesRaw].sort((a, b) => (a.hizmetAdi || '').localeCompare(b.hizmetAdi || '', 'tr')),
        [servicesRaw]
    );

    // Çoklu seçim için Set kullanımı
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Arama filtreleme (state + effect yerine türetilmiş değer: veri cache'ten
    // geldiği için her iki liste de her zaman tutarlı; eskiden yalnızca aktif
    // sekmenin listesi güncelleniyordu).
    const lowerTerm = searchTerm.toLowerCase();
    const filteredStocks = useMemo(
        () => stocks.filter(s => (s.urunAdi || '').toLowerCase().includes(lowerTerm)),
        [stocks, lowerTerm]
    );
    const filteredServices = useMemo(
        () => services.filter(s => (s.hizmetAdi || '').toLowerCase().includes(lowerTerm)),
        [services, lowerTerm]
    );

    // Modal açıldığında reset
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedIds(new Set());
            setCurrentTab(0);
        }
    }, [isOpen]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSearchTerm('');
    };

    const toggleSelection = (item) => {
        const newSelectedIds = new Set(selectedIds);
        if (newSelectedIds.has(item.id)) {
            newSelectedIds.delete(item.id);
        } else {
            newSelectedIds.add(item.id);
        }
        setSelectedIds(newSelectedIds);
    };

    const validateAndPrepareItems = () => {
        if (selectedIds.size === 0) {
            return { valid: false, error: 'Lütfen en az bir ürün veya hizmet seçiniz.' };
        }

        const itemsToAdd = [];

        // Seçili stokları bul ve ekle
        stocks.forEach(stock => {
            if (selectedIds.has(stock.id)) {
                itemsToAdd.push({
                    tip: 'urun',
                    referansId: stock.id,
                    ad: stock.urunAdi,
                    miktar: 1, // Varsayılan miktar
                    birimFiyat: Number(stock.satisFiyati || 0),
                    toplamFiyat: Number(stock.satisFiyati || 0),
                    stokId: stock.id,
                    mevcutStok: stock.stokAdedi
                });
            }
        });

        // Seçili hizmetleri bul ve ekle
        services.forEach(service => {
            if (selectedIds.has(service.id)) {
                itemsToAdd.push({
                    tip: 'hizmet',
                    referansId: service.id,
                    ad: service.hizmetAdi,
                    miktar: 1,
                    birimFiyat: Number(service.fiyat || 0),
                    toplamFiyat: Number(service.fiyat || 0)
                });
            }
        });

        return { valid: true, items: itemsToAdd };
    };

    const reset = () => {
        setSelectedIds(new Set());
    };

    return {
        currentTab,
        searchTerm,
        setSearchTerm,
        filteredStocks,
        filteredServices,
        selectedIds,
        handleTabChange,
        toggleSelection,
        validateAndPrepareItems, // Updated
        reset
    };
};
